import type { FocusEvent, ReactNode } from "react";

import type { SceneCopy } from "../content";
import "./TourScene.css";

/** Los tres capítulos del recorrido (antes / durante / después). Los ids son
 * las anclas de las píldoras de `TourIntro` y deben coincidir con los
 * `Chapter` que monta la página. */
const TOUR_CHAPTERS: { id: string; number: number; label: string }[] = [
  { id: "antes", number: 1, label: "Antes del torneo" },
  { id: "durante", number: 2, label: "Durante el torneo" },
  { id: "despues", number: 3, label: "Después del torneo" },
];

/** En celular las píldoras van en un carril que se desliza de lado. Chrome no
 * mueve el carril al enfocar una píldora que ya se asoma, así que con el
 * teclado quedaba cortada contra el borde: se alinea su casilla al inicio del
 * carril (un punto de ajuste del carril, así no rebota). */
function revealPill(event: FocusEvent<HTMLAnchorElement>) {
  const pill = event.currentTarget;
  const list = pill.closest<HTMLElement>(".t123-tourintro__pills");
  if (!list || list.scrollWidth <= list.clientWidth) return;
  const listRect = list.getBoundingClientRect();
  const pillRect = pill.getBoundingClientRect();
  if (pillRect.left >= listRect.left && pillRect.right <= listRect.right) return;
  pill.parentElement?.scrollIntoView?.({ block: "nearest", inline: "start" });
}

/** Introducción del recorrido (#recorrido): título centrado y píldoras que
 * llevan a cada capítulo (anclas, no fijas). */
export function TourIntro() {
  return (
    <section id="recorrido" className="t123-section t123-tourintro" aria-labelledby="t123-tourintro-title">
      <div className="t123-shell">
        <div className="t123-heading-center t123-tourintro__head">
          <span className="t123-kicker">Recorrido</span>
          <h2 id="t123-tourintro-title" className="t123-h2">
            Así se ve tu torneo en Torneos123
          </h2>
          <p className="t123-lead">
            De la convocatoria a la premiación, todo en el mismo lugar. Las pantallas usan datos de ejemplo.
          </p>
        </div>
        <nav className="t123-tourintro__nav" aria-label="Capítulos del recorrido">
          <ol className="t123-tourintro__pills t123-scroll-x">
            {TOUR_CHAPTERS.map((chapter) => (
              <li key={chapter.id}>
                <a className="t123-tourintro__pill" href={`#${chapter.id}`} onFocus={revealPill}>
                  <span className="t123-tourintro__num" aria-hidden="true">
                    {chapter.number}
                  </span>
                  <span>{chapter.label}</span>
                </a>
              </li>
            ))}
          </ol>
        </nav>
      </div>
    </section>
  );
}

const CHAPTER_TONE_CLASS: Record<"white" | "soft" | "green", string> = {
  white: "",
  soft: " t123-section--soft",
  green: " t123-section--green",
};

/** Capítulo del recorrido: número en una pelota lima, etiqueta y H2; debajo,
 * sus escenas. */
export function Chapter({
  id,
  number,
  label,
  title,
  tone,
  children,
}: {
  id: string;
  number: number;
  label: string;
  title: string;
  tone: "white" | "soft" | "green";
  children: ReactNode;
}) {
  const titleId = `t123-chapter-${id}-title`;
  return (
    <section
      id={id}
      className={`t123-section t123-chapter t123-chapter--${tone}${CHAPTER_TONE_CLASS[tone]}`}
      aria-labelledby={titleId}
    >
      <div className="t123-shell">
        <header className="t123-chapter__head">
          <span className="t123-chapter__ball" aria-hidden="true">
            {number}
          </span>
          <div className="t123-chapter__titles">
            <p className="t123-chapter__label">{label}</p>
            <h2 id={titleId} className="t123-h2">
              {title}
            </h2>
          </div>
        </header>
        <div className="t123-chapter__body">{children}</div>
      </div>
    </section>
  );
}

/** Escena del recorrido: etiqueta, H3, párrafo y lista de palomitas junto a
 * su maqueta. `reverse` pone la maqueta a la izquierda en escritorio; `wide`
 * pone el texto arriba y la maqueta a todo lo ancho. */
export function TourScene({
  copy,
  mock,
  reverse = false,
  wide = false,
  extra,
  bulletsColumns = 1,
}: {
  copy: SceneCopy;
  mock: ReactNode;
  reverse?: boolean;
  wide?: boolean;
  extra?: ReactNode;
  bulletsColumns?: 1 | 2;
}) {
  const classes = ["t123-scene"];
  if (reverse && !wide) classes.push("t123-scene--reverse");
  if (wide) classes.push("t123-scene--wide");
  return (
    <div className={classes.join(" ")}>
      <div className="t123-scene__copy">
        <div className="t123-scene__head">
          <p className="t123-scene__kicker">{copy.kicker}</p>
          <h3>{copy.title}</h3>
          <p className="t123-scene__lead">{copy.lead}</p>
        </div>
        <ul className={`t123-checklist t123-scene__list${bulletsColumns === 2 ? " t123-scene__list--two" : ""}`}>
          {copy.bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
        {extra ? <div className="t123-scene__extra">{extra}</div> : null}
      </div>
      <div className="t123-scene__mock">{mock}</div>
    </div>
  );
}
